import { documentCookie, withXsrfHeader } from './xsrf-header';

/**
 * A dashboard card's direct write: the frame posts `{kind:'act'}`, and the HOST asks the harness to prepare
 * it, draws the confirm itself and executes it. The harness's public `HarnessService` takes these calls only
 * from the metasite owner's own session, so they go from the browser, same origin, with the user's cookie —
 * a gateway route like the dashboard render, not the app mount (`API_BASE`), and never through a server route
 * that would carry another identity.
 *
 * No runtime imports beyond `xsrf-header`: tests drive it directly (orion-web/CLAUDE.md, "a unit test must
 * not import the app's dependency graph").
 */
const CARD_ACTIONS_BASE = '/_api/symphony-harness/v1/card-actions';

export interface CardActionRequest {
  dashboardSlug: string;
  widgetSlug: string;
  op: string;
  record: string;
  /** The component version the card was rendered from. Prepare refuses with CARD_CHANGED unless it is current. */
  componentVersion: number;
}

/** PrepareCardAction's and UndoPrepareCardAction's answer. `token` is a credential: it never enters the frame. */
export interface PreparedCardAction {
  title: string;
  lines: string[];
  destructive: boolean;
  customerNotice: string;
  /** The op can skip what Wix tells the customer (a cancellation's email): the dialog offers a switch for it,
   *  on by default. False for every op that cannot, and the JSON leaves it out then. */
  offersCustomerNotification: boolean;
  reversible: boolean;
  token: string;
  refused: boolean;
  reason: string;
  fallbackPrompt: string;
}

export interface ExecutedCardAction {
  ok: boolean;
  undoToken: string;
  refused: boolean;
  reason: string;
}

/** Not a harness code: the call was turned away or failed before the harness answered, so nothing ran. */
export const UNAVAILABLE = 'UNAVAILABLE';
/** The harness's code for a write Wix never answered, which may have happened; also used when execute's own call fails mid-flight. */
export const OUTCOME_UNKNOWN = 'OUTCOME_UNKNOWN';

/** The frame's `record`, as the request carries it: the bridge sends a scalar, anything else is no record. */
export const actRecord = (record: unknown): string =>
  typeof record === 'string' || typeof record === 'number' || typeof record === 'boolean' ? String(record) : '';

/** The frame's `version`: an integer, else 0, which prepare refuses as a changed card. */
export const actVersion = (version: unknown): number => (Number.isInteger(version) ? (version as number) : 0);

/** The harness's code for a token that is gone: expired, consumed, or never issued. An undo token is otherwise
 *  still good after a refused undo-prepare, so only this one ends the Undo offer. */
export const TOKEN_INVALID = 'TOKEN_INVALID';

/** Not a harness code: the host refused the act itself, because another write or a widget confirm is open, or its
 *  dashboard is hidden. */
export const BUSY = 'BUSY';
/** The harness's code for a malformed request; the host uses it for an act it refuses before any call. */
export const INVALID_REQUEST = 'INVALID_REQUEST';

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MAX_OP_LENGTH = 100;

/**
 * What the host keeps of a frame's act before anything leaves it. Every record a card acts on is a booking or
 * an order id, so a record that is not a GUID is refused here, without a call, and is never logged: a card
 * script can put anything in it, an email or a phone number among them. `op` is cut to the harness's limit
 * for the same reason; a longer one is refused.
 */
export function checkedAct(op: string, record: string): { op: string; record: string; valid: boolean } {
  const recordOk = GUID.test(record);
  return {
    op: op.slice(0, MAX_OP_LENGTH),
    record: recordOk ? record : '',
    valid: recordOk && op.length > 0 && op.length <= MAX_OP_LENGTH,
  };
}

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

// The proto's JSON leaves out every field at its default, so a missing one is its zero value.
const toPrepared = (raw: Record<string, unknown>): PreparedCardAction => ({
  title: text(raw.title),
  lines: Array.isArray(raw.lines) ? raw.lines.filter((line): line is string => typeof line === 'string') : [],
  destructive: raw.destructive === true,
  customerNotice: text(raw.customerNotice),
  offersCustomerNotification: raw.offersCustomerNotification === true,
  reversible: raw.reversible === true,
  token: text(raw.token),
  refused: raw.refused === true || !text(raw.token),
  reason: text(raw.reason),
  fallbackPrompt: text(raw.fallbackPrompt),
});

const refusedPrepare = (reason: string): PreparedCardAction => ({
  title: '',
  lines: [],
  destructive: false,
  customerNotice: '',
  offersCustomerNotification: false,
  reversible: false,
  token: '',
  refused: true,
  reason,
  fallbackPrompt: '',
});

type Posted = { ok: true; body: Record<string, unknown> } | { ok: false; status: number };

async function post(method: string, body: object): Promise<Posted> {
  try {
    const res = await fetch(
      `${CARD_ACTIONS_BASE}/${method}`,
      withXsrfHeader(
        { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
        documentCookie(),
      ),
    );
    if (!res.ok) return { ok: false, status: res.status };
    const json: unknown = await res.json();
    return { ok: true, body: json && typeof json === 'object' ? (json as Record<string, unknown>) : {} };
  } catch {
    return { ok: false, status: 0 };
  }
}

export async function prepareCardAction(request: CardActionRequest): Promise<PreparedCardAction> {
  const res = await post('prepare', { ...request, args: {} });
  return res.ok ? toPrepared(res.body) : refusedPrepare(UNAVAILABLE);
}

export async function undoPrepareCardAction(undoToken: string): Promise<PreparedCardAction> {
  const res = await post('undo-prepare', { undoToken });
  return res.ok ? toPrepared(res.body) : refusedPrepare(UNAVAILABLE);
}

/**
 * `skipCustomerNotification` is the owner turning off the dialog's "Notify the customer" switch, which is only
 * offered when prepare said `offersCustomerNotification`. It is sent only when true: the harness reads a missing
 * field as "notify", so every op without the switch sends exactly what it did before.
 */
export async function executeCardAction(
  token: string,
  { skipCustomerNotification = false }: { skipCustomerNotification?: boolean } = {},
): Promise<ExecutedCardAction> {
  const res = await post('execute', skipCustomerNotification ? { token, skipCustomerNotification: true } : { token });
  if (!res.ok) {
    // A 4xx is the gateway or the harness turning the call away before it ran; anything else may have
    // reached Wix, and saying "nothing changed" about a payment that went through is the worse error.
    const refusedUpfront = res.status >= 400 && res.status < 500;
    return { ok: false, undoToken: '', refused: true, reason: refusedUpfront ? UNAVAILABLE : OUTCOME_UNKNOWN };
  }
  const ok = res.body.ok === true;
  return {
    ok,
    undoToken: ok ? text(res.body.undoToken) : '',
    refused: !ok,
    reason: ok ? '' : text(res.body.reason) || OUTCOME_UNKNOWN,
  };
}

export type CardActionStage = 'shown' | 'confirmed' | 'cancelled' | 'refused' | 'done' | 'undone';

export interface CardActionBiEvent {
  type: 'itemSeen' | 'clickCta';
  componentName: string;
  additionalFields: { op: string; outcome: string; record: string };
}

/**
 * The generic event one step of a card action becomes. The dialog appearing is an impression; every other
 * step is a click-level event named for the step. Op, outcome and record id only: the dialog's lines can
 * carry a customer's name, and they stay out of BI.
 */
export function cardActionBiEvent(stage: CardActionStage, op: string, record: string, outcome: string): CardActionBiEvent {
  return {
    type: stage === 'shown' ? 'itemSeen' : 'clickCta',
    componentName: `card-action-${stage}`,
    additionalFields: { op, outcome, record },
  };
}
